import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/config';
import CreateCalendarModal from './CreateCalendarModal';
import CalendarSuccessModal from './CalendarSuccessModal';
import JoinCalendarModal from './JoinCalendarModal';
import JoinSuccessModal from './JoinSuccessModal';
import CalendarList from './CalendarList';
import UserSettingsModal from './UserSettingsModal';
import './Dashboard.css';

export default function Dashboard() {
  const { currentUser, userRole, logout } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showJoinSuccessModal, setShowJoinSuccessModal] = useState(false);
  const [newCalendar, setNewCalendar] = useState(null);
  const [userData, setUserData] = useState(null);
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

  // Real-time listener for user data
  useEffect(() => {
    if (!currentUser) return;

    const unsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), (docSnapshot) => {
      if (docSnapshot.exists()) {
        setUserData(docSnapshot.data());
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  async function handleShareApp() {
    const url = window.location.origin;
    if (navigator.share) {
      try {
        await navigator.share({ url });
      } catch (_) {}
    } else {
      try {
        await navigator.clipboard.writeText(url);
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      } catch (_) {}
    }
  }

  async function handleLogout() {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  }

  function handleCreateSuccess(calendar) {
    setNewCalendar(calendar);
    setShowCreateModal(false);
    setShowSuccessModal(true);
  }

  function handleSuccessClose() {
    setShowSuccessModal(false);
    setNewCalendar(null);
  }

  function handleJoinSuccess(calendar) {
    setNewCalendar(calendar);
    setShowJoinModal(false);
    setShowJoinSuccessModal(true);
  }

  function handleJoinSuccessClose() {
    setShowJoinSuccessModal(false);
    setNewCalendar(null);
  }

  return (
    <div className="dashboard-wrapper">
      {/* Header */}
      <header className="dashboard-top-header">
        <div className="header-logo">
          <img src={isDark ? "/Dlogo512.png" : "/Clogo192.png"} alt="Calendar" className="header-logo-img" />
          <span>التقويم</span>
        </div>

        <div className="header-user">
          {/* Share App Button - PWA only */}
          {isPWA && <button className="icon-btn" onClick={handleShareApp} title="مشاركة التطبيق">
            {shareCopied ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="18" cy="5" r="3"></circle>
                <circle cx="6" cy="12" r="3"></circle>
                <circle cx="18" cy="19" r="3"></circle>
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
              </svg>
            )}
          </button>}

          {/* Super Admin Link */}
          {userRole === 'superAdmin' && (
            <button className="icon-btn" onClick={() => navigate('/admin')} title="Super Admin Panel">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5"></path>
                <path d="M2 12l10 5 10-5"></path>
              </svg>
            </button>
          )}

          <div className="user-info">
            <div className="user-name">{userData?.displayName || currentUser?.displayName || 'مستخدم'}</div>
            <div className="user-email">{currentUser?.email}</div>
          </div>

          {/* User avatar with dropdown */}
          <div className="user-menu-wrapper">
            <div className="user-avatar" onClick={() => setShowUserMenu(!showUserMenu)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>

            {showUserMenu && (
              <>
                <div className="user-menu-overlay" onClick={() => setShowUserMenu(false)} />
                <div className="user-dropdown">
                  <div className="user-dropdown-header">
                    <div className="user-dropdown-name">{userData?.displayName || currentUser?.displayName || 'مستخدم'}</div>
                    <div className="user-dropdown-email">{currentUser?.email}</div>
                  </div>
                  <div className="user-dropdown-divider" />
                  <button className="user-dropdown-item" onClick={() => { toggleTheme(); setShowUserMenu(false); }}>
                    {isDark ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="5"></circle>
                        <line x1="12" y1="1" x2="12" y2="3"></line>
                        <line x1="12" y1="21" x2="12" y2="23"></line>
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                        <line x1="1" y1="12" x2="3" y2="12"></line>
                        <line x1="21" y1="12" x2="23" y2="12"></line>
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                      </svg>
                    )}
                    <span>{isDark ? 'الوضع النهاري' : 'الوضع الليلي'}</span>
                  </button>
                  <button className="user-dropdown-item" onClick={() => { setShowUserMenu(false); setShowUserSettings(true); }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3"></circle>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                    </svg>
                    <span>إعدادات الحساب</span>
                  </button>
                  <div className="user-dropdown-divider" />
                  <button className="user-dropdown-item user-dropdown-logout" onClick={() => { setShowUserMenu(false); handleLogout(); }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                    <span>تسجيل الخروج</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
  <div className="welcome-section">
    <div className="welcome-icon">
      <img src={isDark ? "/Dlogo512.png" : "/Clogo192.png"} alt="Calendar" className="welcome-logo-img" />
    </div>
    <h1 className="welcome-title">مرحباً بك في التقويم </h1>
    <p className="welcome-subtitle">نظام حجز ذكي لإدارة المواعيد مع عائلتك أو فريقك</p>
  </div>

  {/* Calendar List - Shows user's calendars */}
  <CalendarList />

  <div className="action-cards">
    {/* Create New Calendar */}
    <div className="action-card create-card" onClick={() => setShowCreateModal(true)}>
      <div className="card-icon create-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
      </div>
      <h3 className="card-title">إنشاء مجموعة جديدة</h3>
      <p className="card-description">أنشئ تقويماً خاصاً وابدأ الدعوة للآخرين للانضمام</p>
      <button className="card-action-link create-link">
        ابدأ الآن ←
      </button>
    </div>

    {/* Join Calendar */}
    <div className="action-card join-card" onClick={() => setShowJoinModal(true)}>
      <div className="card-icon join-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
          <polyline points="10 17 15 12 10 7"></polyline>
          <line x1="15" y1="12" x2="3" y2="12"></line>
        </svg>
      </div>
      <h3 className="card-title">الانضمام لمجموعة</h3>
      <p className="card-description">أدخل رمز الدعوة للانضمام إلى تقويم موجود</p>
      <button className="card-action-link join-link">
        أدخل الرمز ←
      </button>
    </div>
  </div>

  <p className="footer-note">يمكنك الانضمام لعدة مجموعات وإدارتها جميعاً من لوحة التحكم</p>
</main>

      {/* Create Calendar Success Modal */}
      {showSuccessModal && newCalendar && (
        <CalendarSuccessModal
          calendar={newCalendar}
          onClose={handleSuccessClose}
        />
      )}

      {/* Join Calendar Success Modal */}
      {showJoinSuccessModal && newCalendar && (
        <JoinSuccessModal
          calendar={newCalendar}
          onClose={handleJoinSuccessClose}
        />
      )}

      {/* Create Calendar Modal */}
      {showCreateModal && (
        <CreateCalendarModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}

      {/* Join Calendar Modal */}
      {showJoinModal && (
        <JoinCalendarModal
          onClose={() => setShowJoinModal(false)}
          onSuccess={handleJoinSuccess}
        />
      )}

      {/* User Settings Modal */}
      {showUserSettings && (
        <UserSettingsModal
          userData={userData}
          onClose={() => setShowUserSettings(false)}
        />
      )}
    </div>
  );
}